import type { FC } from 'react';
import { Button } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import styles from './scoring-glossary.module.scss';

interface ScoringGlossaryProps {
  onClose: () => void;
}

// The five scoring factors and their weights, mirroring the Story 2 agent.
const FACTORS = [
  {
    name: 'Operator Naming',
    weight: '0.35',
    description:
      "Match between the operator's target-zone naming convention and the candidate interval.",
  },
  {
    name: 'Nearby Well Patterns',
    weight: '0.25',
    description: 'Landing zones of nearby drilled wells in the same area.',
  },
  {
    name: 'Operator Area',
    weight: '0.20',
    description:
      "Consistency with the operator's recent wells across the area.",
  },
  {
    name: 'Recent Permit Activity',
    weight: '0.10',
    description: 'Target zones on recent permits in the vicinity.',
  },
  {
    name: 'Recent Drilling',
    weight: '0.10',
    description: 'Target zones of the most recently drilled nearby wells.',
  },
];

// Read-only reference (right sidebar): how the confidence score is composed.
export const ScoringGlossary: FC<ScoringGlossaryProps> = ({ onClose }) => (
  <div className={styles.panel}>
    <div className={styles.header}>
      <div>
        <h2 className={styles.title}>Scoring Glossary</h2>
        <p className={styles.subtitle}>
          How the agent evaluates each candidate target zone
        </p>
      </div>
      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        renderIcon={Close}
        iconDescription="Close"
        onClick={onClose}
      />
    </div>

    <div className={styles.body}>
      <section className={styles.block}>
        <h3 className={styles.blockTitle}>Confidence</h3>
        <p className={styles.blockText}>
          A 0–100% score for how strongly the evidence supports a candidate
          target zone. Higher means more agreement across the five factors.
        </p>
      </section>

      <section className={styles.block}>
        <h3 className={styles.blockTitle}>How the score is built</h3>
        <code className={styles.formula}>
          0.35·naming + 0.25·patterns + 0.20·area + 0.10·permits + 0.10·drilling
        </code>
      </section>

      <section className={styles.block}>
        <h3 className={styles.blockTitle}>The five factors</h3>
        <dl className={styles.factorList}>
          {FACTORS.map((factor) => (
            <div key={factor.name} className={styles.factor}>
              <dt className={styles.factorName}>
                {factor.name}
                <span className={styles.factorWeight}>{factor.weight}</span>
              </dt>
              <dd className={styles.factorText}>{factor.description}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  </div>
);
